import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { z } from 'zod';
import { PERMISSION_PARKING_TAG } from '../../Services/PermissionsService';
import { requirePermission } from '../../router/RouteGuardsService';
import ParkingTagForm from '../../components/Codebooks/ParkingTagForm';
import { PARKING_TAG_TABLE_KEY } from '../../components/Codebooks/constants';
import {
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../../components/CmdTable/CmdTableState';
import { getForm } from '../../components/CmdTable/CmdTableService';
import { TABLE_CODEBOOKS_PARKING } from '../../Services/TableService';
import { selectedCustomersAtom } from '../../store/jotai/customers';
import { URL_CODEBOOKS } from '../../router/constants';

const editParkingTagSearchSchema = z.object({
  customerId: z.coerce.number().optional(),
});

export const Route = createFileRoute('/_authenticated/system_settings/parking-tag/edit/$id')({
  validateSearch: editParkingTagSearchSchema,
  beforeLoad: async ({ context }) => {
    await requirePermission(PERMISSION_PARKING_TAG, ['update'], '/unauthorized', context);
    return {
      ...context,
      documentTitle: {
        main: 'table.detail',
        suffix: 'tab.parking-tag',
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: EditParkingTagRoute,
});

function EditParkingTagRoute() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedCustomers = useAtomValue(selectedCustomersAtom);
  const filterForRequest = useAtomValue(filterForRequestAtomFamily(PARKING_TAG_TABLE_KEY));
  const sortFilter = useAtomValue(sortFilterAtomFamily(PARKING_TAG_TABLE_KEY));
  const tagId = Number(id);
  const customerId = search.customerId ?? null;

  const { data, isLoading } = useQuery({
    queryKey: ['parkingTagForm', tagId, customerId],
    queryFn: () => getForm(TABLE_CODEBOOKS_PARKING, tagId, customerId ?? undefined),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleClose = React.useCallback(() => {
    navigate({ to: `${URL_CODEBOOKS}/${TABLE_CODEBOOKS_PARKING}` });
  }, [navigate]);

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PARKING_TAG_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [filterForRequest, queryClient, selectedCustomers, sortFilter]);

  if (isLoading || !data?.length) {
    return null;
  }

  return (
    <ParkingTagForm
      id={tagId}
      customerId={customerId}
      data={data}
      updateTable={updateTable}
      handleClose={handleClose}
    />
  );
}
